"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import dayjs from "dayjs";
import ja from "dayjs/locale/ja";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import { graphql } from "gql.tada";
import {
  CheckCircle2Icon,
  CircleIcon,
  DownloadIcon,
  Loader2Icon,
  PackageOpenIcon,
} from "lucide-react";
import { useContext, useEffect, useRef } from "react";
import { useQuery } from "urql";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/[lang]/[workspaceId]/_components/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSafeI18nDate } from "@/i18n/use-safe-i18n-date";
import { logger } from "@/lib/logger";
import { ExportStatusContext } from "./export-status-context";

dayjs.extend(duration);
dayjs.extend(relativeTime);

export default function ExportStatus({ workspaceId }: { workspaceId: string }) {
  const { isOpen, onOpen, onClose } = useContext(ExportStatusContext);
  const { i18n } = useLingui(); // Import i18n instance

  const { safeI18nDate } = useSafeI18nDate();

  const [{ fetching, data, error }, executeQuery] = useQuery({
    query: graphql(`
    query ExportStatus($organizationId: ID!) {
      organization: node(id: $organizationId) {
        id
        __typename
        ... on Organization {
          projects {
            __typename
            id
            name
            color
            ... on SearchAnalyticsProject {
              id
              searchAnalytics {
                id
                __typename
                exportData {
                  __typename
                  urls
                  expires
                  status
                  exportType
                  exportedAt
                }
              }
            }
            ... on UserAnalyticsProject {
              id
              userAnalytics{
                id
                __typename
                exportData {
                  __typename
                  urls
                  expires
                  status
                  exportType
                  exportedAt
                }
              }
            }
            ... on AdsAnalyticsProject {
              adsAnalytics {
                id
                __typename
                exportData {
                  __typename
                  urls
                  expires
                  status
                  exportType
                  exportedAt
                }
              }
            }
          }
        }
      }
    }
  `),
    variables: {
      organizationId: workspaceId,
    },
    requestPolicy: "cache-and-network",
  });

  // ポーリング
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (!isOpen) return;
      executeQuery();
    }, 1000);
  }, [executeQuery, isOpen]);
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  if (error) {
    logger.error(error);
    return (
      <Button disabled variant="outline" size="icon-sm">
        <DownloadIcon className="w-4 h-4" />
        <span className="sr-only">
          <Trans>Export Status</Trans>
        </span>
      </Button>
    );
  }

  const org =
    data?.organization?.__typename === "Organization"
      ? data.organization
      : null;

  const saProjects = org?.projects.filter(
    (project) => project.__typename === "SearchAnalyticsProject",
  );

  const uaProjects = org?.projects.filter(
    (project) => project.__typename === "UserAnalyticsProject",
  );

  const adsProjects = org?.projects.filter(
    (project) => project.__typename === "AdsAnalyticsProject",
  );

  const tableData: {
    projectName: string;
    projectColor: string;
    projectType: string;
    exportedAt: string;
    exportType:
      | "CSV"
      | "JSON"
      | "ECOMMERCE_PERFORMANCE_DETAIL"
      | "ITEM_PERFORMANCE"
      | "TRANSACTIONS";
    status: "DONE" | "IN_PROGRESS" | "ERROR";
    expires: string;
    isExpired: boolean;
    urls: string[];
  }[] = [
    ...(saProjects?.flatMap(
      (project) =>
        project.searchAnalytics?.exportData?.map((data) => ({
          projectName: project.name,
          projectColor: project.color,
          projectType: "Search Analytics", // Keep as is, technical identifier
          exportedAt: data.exportedAt,
          exportType: data.exportType,
          status: data.status,
          expires: data.expires ?? "",
          isExpired: dayjs(data.expires).isBefore(dayjs()),
          urls: data.urls ?? [],
        })) ?? [],
    ) ?? []),
    ...(uaProjects?.flatMap(
      (project) =>
        project.userAnalytics?.exportData?.map((data) => ({
          projectName: project.name,
          projectColor: project.color,
          projectType: "User Analytics", // Keep as is, technical identifier
          exportedAt: data.exportedAt,
          exportType: data.exportType,
          status: data.status,
          expires: data.expires ?? "",
          isExpired: dayjs(data.expires).isBefore(dayjs()),
          urls: data.urls ?? [],
        })) ?? [],
    ) ?? []),
    ...(adsProjects?.flatMap(
      (project) =>
        project.adsAnalytics?.exportData?.map((data) => ({
          projectName: project.name,
          projectColor: project.color,
          projectType: "Ads Analytics",
          exportedAt: data.exportedAt,
          exportType: data.exportType,
          status: data.status,
          expires: data.expires ?? "",
          isExpired: dayjs(data.expires).isBefore(dayjs()),
          urls: data.urls ?? [],
        })) ?? [],
    ) ?? []),
  ].toSorted((a, b) => {
    if (a.exportedAt && b.exportedAt) {
      return dayjs(b.exportedAt).isAfter(dayjs(a.exportedAt)) ? 1 : -1;
    }
    return 0;
  });

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="outline" size="icon-sm" onClick={onOpen}>
            <DownloadIcon className="w-4 h-4" />
            <span className="sr-only">
              <Trans>Export Status</Trans>
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            <Trans>Export Status</Trans>
          </p>
        </TooltipContent>
      </Tooltip>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-(--breakpoint-lg)">
          <DialogHeader>
            <DialogTitle>
              <Trans>Export Status</Trans>
            </DialogTitle>
          </DialogHeader>

          {fetching ? (
            <div className="flex flex-col gap-4 items-center justify-center min-h-[200px]">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
            </div>
          ) : tableData.length === 0 ? (
            <div className="flex flex-col gap-4 items-center justify-center min-h-[200px]">
              <PackageOpenIcon className="w-10 h-10" />
              <p className="font-bold">
                <Trans>No export data</Trans>
              </p>
              <p className="text-sm text-muted-foreground max-w-sm text-center">
                <Trans>
                  You can export Search Analytics, User Analytics, and Ads
                  Analytics data <br />
                  from the view details page.
                </Trans>
              </p>
            </div>
          ) : (
            <ScrollArea className="max-h-[80vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Trans>Project</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Type</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Exported At</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Format</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Status</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Expires In</Trans>
                    </TableHead>
                    <TableHead>
                      <Trans>Download Links</Trans>
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {tableData.map((exportData, i) => (
                    <TableRow key={i}>
                      <TableCell className="flex mt-2 items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded-sm`}
                          style={{ backgroundColor: exportData.projectColor }}
                        />
                        {exportData.projectName}
                      </TableCell>
                      <TableCell>{exportData.projectType}</TableCell>
                      <TableCell>
                        {safeI18nDate(exportData.exportedAt, {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })}
                      </TableCell>
                      <TableCell>{exportData.exportType}</TableCell>
                      <TableCell>
                        {exportData.status === "DONE" ? (
                          <>
                            <CheckCircle2Icon className="w-4 h-4 text-green-600 dark:text-green-400" />
                            <span className="sr-only">
                              <Trans>Exported</Trans>
                            </span>
                          </>
                        ) : exportData.status === "IN_PROGRESS" ? (
                          <>
                            <Loader2Icon className="w-4 h-4 animate-spin" />
                            <span className="sr-only">
                              <Trans>Exporting</Trans>
                            </span>
                          </>
                        ) : exportData.status === "ERROR" ? (
                          <>
                            <CircleIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                            <span className="sr-only">
                              <Trans>Error</Trans>
                            </span>
                          </>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {exportData.isExpired ? (
                          <span className="text-destructive">
                            <Trans>Expired</Trans>
                          </span>
                        ) : (
                          humanizeRemainingTime(i18n.locale, exportData.expires)
                        )}
                      </TableCell>
                      <TableCell>
                        {exportData.isExpired
                          ? ""
                          : exportData.urls?.map((url, i) => (
                              <Button asChild key={i} variant="link">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <DownloadIcon className="w-4 h-4" />
                                  <Trans>Download Link {i + 1}</Trans>
                                </a>
                              </Button>
                            ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function humanizeRemainingTime(locale: string, expiredAt: string | null) {
  if (!expiredAt) {
    return "";
  }
  if (locale === "ja") {
    dayjs.locale(ja);
  } else {
    dayjs.locale("en");
  }
  const now = dayjs();
  const remaining = dayjs.duration(dayjs(expiredAt).diff(now));
  const humanizedTime = remaining.humanize();
  return humanizedTime;
}
